import { canPerformAction } from '../constants/permissions'
import type { Action } from '../constants/actions'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { useRolePermissions } from '@/features/roles/hooks/use-role-permissions'

/**
 * Hook to check user permissions in React components
 *
 * Provides a simple `can(action)` function to check if the current user
 * has permission to perform a specific action.
 *
 * @example
 * ```tsx
 * import { usePermissions } from '@/shared/hooks/use-permissions'
 * import { ACTIONS } from '@/shared/constants/actions'
 *
 * function ProductActions() {
 *   const { can } = usePermissions()
 *
 *   return (
 *     <div>
 *       {can(ACTIONS.PRODUCT_EDIT) && <Button>Edit</Button>}
 *       {can(ACTIONS.PRODUCT_DELETE) && <Button>Delete</Button>}
 *     </div>
 *   )
 * }
 * ```
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { permissionsByRole, isPending } = useRolePermissions({
    tenantId: user?.tenantId ?? null,
    enabled: Boolean(user?.tenantId),
  })

  /**
   * True while an answer is not yet trustworthy: after a hard reload the tokens are restored
   * synchronously but the profile (and with it the roles) arrives from /me a moment later, and
   * the per-tenant role permissions are their own request. In that window `can` would return
   * false for everyone — callers should show a loading state, not "Zugriff verweigert".
   */
  const isLoading =
    (isAuthenticated && !user) || (Boolean(user?.tenantId) && isPending)

  /**
   * Check if the current user can perform the specified action
   *
   * @param action - The action to check permission for
   * @returns true if user has permission, false otherwise (including when no user is logged in)
   */
  const can = (action: Action): boolean => {
    if (!user) return false
    return canPerformAction(user.roles, action, permissionsByRole)
  }

  return { can, user, isLoading }
}
